import { useEffect } from 'react';
import { KeyRecoilProps, TPickDateAction } from '@/shared/model';
import {
  selectControlModal,
  selectIsActiveTab,
  selectPickedBookmarkDate,
  selectPickedEndDate,
  selectPickedStartDate,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';

/**
 * Runs the beat tab's modal and date-picking side effects. Closes the control modal when the selected strip changes or the tab goes inactive, also resetting the HR summary modal, advances the pick-date state through END and NONE as start and end dates fill in, and clears the picked dates or re-arms START or BOOKMARK whenever the control-modal flags change.
 *
 * @param keyRecoil - The tab key selecting which report tab's state these effects drive.
 * @returns Nothing; this hook only runs the modal/date-picking effects.
 */
const useTabBeatUIEffect = ({ keyRecoil }: KeyRecoilProps) => {
  const setPickDateActionAction = useReportStore((s) => s.actions.setPickDateAction);
  /**
   * Records which end of a date range the beat tab is currently picking. Forwards the value to the report store action along with this tab's key.
   *
   * @param v - The pick-date action state to store.
   * @returns The result of the store's pick-date action.
   */
  const setPickDateAction = (v: Parameters<typeof setPickDateActionAction>[1]) =>
    setPickDateActionAction(keyRecoil, v);
  const pickedStartDate = useReportStore(selectPickedStartDate(keyRecoil));
  const setPickedStartDateAction = useReportStore((s) => s.actions.setPickedStartDate);
  /**
   * Stores the start timestamp picked on the beat tab. Forwards the value to the report store action bound to this tab's key.
   *
   * @param v - The start timestamp to store, or null to clear it.
   * @returns The result of the store's start-date action.
   */
  const setPickedStartDate = (v: Parameters<typeof setPickedStartDateAction>[1]) =>
    setPickedStartDateAction(keyRecoil, v);
  const pickedEndDate = useReportStore(selectPickedEndDate(keyRecoil));
  const setPickedEndDateAction = useReportStore((s) => s.actions.setPickedEndDate);
  /**
   * Stores the end timestamp picked on the beat tab. Forwards the value to the report store action bound to this tab's key.
   *
   * @param v - The end timestamp to store, or null to clear it.
   * @returns The result of the store's end-date action.
   */
  const setPickedEndDate = (v: Parameters<typeof setPickedEndDateAction>[1]) =>
    setPickedEndDateAction(keyRecoil, v);
  const pickedBookmarkDate = useReportStore(selectPickedBookmarkDate(keyRecoil));
  const setPickedBookmarkDateAction = useReportStore((s) => s.actions.setPickedBookmarkDate);
  /**
   * Stores the timestamp picked for a new ECG bookmark on the beat tab. Forwards the value to the report store action bound to this tab's key.
   *
   * @param v - The bookmark timestamp to store, or null to clear it.
   * @returns The result of the store's bookmark-date action.
   */
  const setPickedBookmarkDate = (v: Parameters<typeof setPickedBookmarkDateAction>[1]) =>
    setPickedBookmarkDateAction(keyRecoil, v);
  const controlModal = useReportStore(selectControlModal(keyRecoil));
  const resetControlModalAction = useReportStore((s) => s.actions.resetControlModal);
  /**
   * Clears the beat tab's modal control flags back to their defaults. Calls the report store's reset action bound to this tab's key.
   *
   * @returns The result of the store's reset action.
   */
  const resetControlModal = () => resetControlModalAction(keyRecoil);
  const resetControlHrSummaryModal = useReportStore((s) => s.actions.resetControlHrSummaryModal);
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));

  //* Close modal when change tab/strip
  useEffect(() => {
    if (!controlModal.isShowNewEvents && !controlModal.isShowAddNewEvent) {
      resetControlModal();
    }
  }, [selectedStrip]);

  //* Close modal when change tab
  useEffect(() => {
    if (!isActiveTab) {
      resetControlModal();
      resetControlHrSummaryModal();
    }
  }, [isActiveTab]);

  //* Pick date handler
  useEffect(() => {
    if (pickedStartDate && !pickedEndDate) {
      setPickDateAction(TPickDateAction.END);
    }
    if (pickedStartDate && pickedEndDate) {
      setPickDateAction(TPickDateAction.NONE);
    }
    if (!pickedStartDate && pickedEndDate) {
      setPickDateAction(TPickDateAction.NONE);
    }
  }, [pickedStartDate, pickedEndDate]);

  useEffect(() => {
    if (pickedBookmarkDate) {
      setPickDateAction(TPickDateAction.NONE);
    }
  }, [pickedBookmarkDate]);

  //* Change duration and Add new event modal have date picker => reset pick date state when close these modal
  useEffect(() => {
    if (Object.keys(controlModal).length > 1) {
      if (
        !(
          controlModal.isShowChangeDurationButtonType &&
          controlModal.isShowAddNewEvent &&
          controlModal.isShowAddEcgBookmark
        )
      ) {
        setPickedStartDate(null);
        setPickedEndDate(null);
        setPickedBookmarkDate(null);
        setPickDateAction(TPickDateAction.NONE);
      }
      if (controlModal.isShowChangeDurationButtonType || controlModal.isShowAddNewEvent) {
        setPickDateAction(TPickDateAction.START);
      }
      if (controlModal.isShowAddEcgBookmark) {
        setPickDateAction(TPickDateAction.BOOKMARK);
      }
    }
  }, [controlModal]);
};

export default useTabBeatUIEffect;
